<script setup lang="ts">
import { computed } from "vue";

import { useNotificationsStore } from "@/stores/notifications";

/**
 * Port de `.toast` (design-reference/styles.css).
 *
 * Purement réactif à l'état du store `notifications` : celui-ci porte la
 * file d'attente et la temporisation d'auto-fermeture, ce composant ne fait
 * qu'afficher la notification courante.
 */

const notifications = useNotificationsStore();

const isVisible = computed(() => notifications.current !== null);
const icon = computed(() => (notifications.current?.tone === "error" ? "!" : "♡"));
const toneClass = computed(() =>
  notifications.current ? `toast--${notifications.current.tone}` : "",
);
</script>

<template>
  <div
    class="toast"
    :class="[toneClass, { 'is-visible': isVisible }]"
    data-testid="toast"
    :data-tone="notifications.current?.tone"
    role="status"
    aria-live="polite"
  >
    <template v-if="notifications.current">
      <span class="toast__icon" aria-hidden="true">{{ icon }}</span>
      <p>{{ notifications.current.message }}</p>
    </template>
  </div>
</template>

<style scoped>
.toast {
  position: fixed;
  right: 16px;
  bottom: max(18px, env(safe-area-inset-bottom));
  left: 16px;
  z-index: 110;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 390px;
  margin: auto;
  padding: 14px 17px;
  color: white;
  border-radius: 14px;
  background: var(--color-olive);
  box-shadow: var(--shadow-sheet);
  opacity: 0;
  pointer-events: none;
  transform: translateY(15px);
  transition: 0.35s ease;
}

.toast.is-visible {
  opacity: 1;
  transform: none;
}

.toast--error {
  background: var(--color-rose-strong);
}

.toast__icon {
  color: var(--color-rose-light);
  font-family: var(--font-serif);
  font-size: 26px;
}

.toast p {
  margin: 0;
  font-size: 12px;
}
</style>
